import React, {useState, useEffect} from 'react'
import {ModulePanel, ModuleHeader, ModuleBody, ModuleFooter} from 'containers/purchase/modules/module_panel'
import {ListGroup} from 'reactstrap'
import ListItemOption from 'ui/list_item_option'
import { useTranslation } from 'react-i18next'
import {currency} from 'i18n/config'
import {get} from 'lodash'
import parse from 'react-html-parser'
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap'
import cn from 'classnames'

const LanguageFilter = ({languages, languageFilter, setLanguageFilter}) => {
  if (!languages) { return "" }

  return (
    <Nav fill pills justified>
      {languages.map(({id, name}, i) => {
        return (
          <NavItem
            key={id}
            onClick={() => { setLanguageFilter(id) }}
            className={cn([
              languageFilter === id ? ['active', 'bg-lighter'] : 'bg-lighter',
              'd-flex',
              'justify-content-center',
              'rounded',
              'text-uppercase',
              'mt-2',
              i == languages.length - 1 ? 'mr-0' : 'mr-0 mr-md-3',
              'mb-4'
            ])}
            style={{
              cursor: 'pointer',
              borderTop: '1px solid',
              borderLeft: '1px solid',
              borderRight: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'activeTab' === true ? 'var(--dark)' : 'var(--dark)'
            }}
          >
            <div
              className={cn([
                'p-2',
                languageFilter === id ? 'bg-primary' : 'bg-light',
                "d-flex"
              ])}
            >
              <i
                className={cn([
                  languageFilter === id ? 'fa-dot-circle text-white' : 'fa-circle text-white',
                  "p-2 align-self-center fa"
                ])}
              />
            </div>
            <div className="w-100 p-2 d-flex justify-content-center align-self-center">
              {name}
            </div>
          </NavItem>
        )
      })}
    </Nav>
  )
}

const ComponentSelect = ({components, component, onComponentSelected}) => {
  const { t, i18n } = useTranslation()

  if (!components || components.length <= 0) {
    return (
      <div className="alert alert-warning">
        {i18n.t('tourcms_component_module.no_availability')}
      </div>
    )
  }

  return (
    <ListGroup>
      {components.map((c) => {
        const {component_key, date_code, note, special_offer_note, total_price, price_breakdown} = c

        const price = get(price_breakdown, 'price_row')

        let priceList = []
        if (Array.isArray(price)) {
          priceList = price
        } else {
          priceList.push(price)
        }

        const componentDescription = (date_code, note, special_offer_note) => {
          let text

          let dateText = date_code

          if (date_code && date_code.includes('NOTSET-NOTSET')) {
            dateText = null
          }

          if (dateText || note) {
            if (dateText && note) {
              text = `${dateText} - ${note}`
            } else if (!dateText) {
              text = note
            } else {
              text = dateText
            }
          } else {
            text = special_offer_note
          }

          return <div>{text}</div>
        }

        return (
          <ListItemOption
            key={component_key}
            active={component.component_key == component_key}
            onClick={() => onComponentSelected(c)}
          >
            <div className={'align-self-center text-center text-md-left'}>
              <div className="font-weight-bold">
                {componentDescription(date_code, note, special_offer_note)}
              </div>
              <div>
                {priceList.map((p) => (
                  <span key={p['rate-id']} className="mr-3">{parse(p.text)}</span>
                ))}
              </div>
            </div>
            <div className={'d-flex align-self-center text-right'}>
              <div className="price text-right font-weight-bold" style={{fontSize: '18px', width: '200'}}>
                {i18n.t('tourcms_component_module.total_price', {price: currency(total_price)})}
              </div>
            </div>
          </ListItemOption>
        )})}
    </ListGroup>
  )
}

const TourcmsComponentModule = (props) => {
  const {machine, config, status} = props
  const { t, i18n } = useTranslation()
  const [current, send] = machine
  const {tickets, components, component} = current.context
  const {external_api_config} = config
  const componentsList = Array.isArray(components) ? components : [components]
  const [languages, setLanguages] = useState([])
  const supplier_note_group = external_api_config && external_api_config.supplier_note_group

  useEffect(() => {
    if (supplier_note_group == 'language' && componentsList.length > 0 ) {
      setLanguages(componentsList[0].languages)
      setLanguageFilter(componentsList[0].language.id)
    }
  }, [components])

  const [languageFilter, setLanguageFilter] = useState(languages[0])

  const isFetching = current.matches({tourcmsComponentStep: 'fetchingComponents'}) ? true : false

  const onComponentSelected = (component) => send({type: 'COMPONENT_SELECTION', component: component})

  let filteredComponents = componentsList

  if (languageFilter) {
   filteredComponents = filteredComponents.filter(({language}) => language.id == languageFilter)
  }

  useEffect(() => onComponentSelected(filteredComponents[0]), [languageFilter])

  return (
    <ModulePanel>
      <ModuleHeader status={status} onClick={() => status == 'completed' ? send('REOPEN_TOURCMSCOMPONENTSTEP') : null}>
        <span>
          <i className="fa fa-star mr-2" aria-hidden="true"></i>
          {i18n.t('tourcms_component_module.select_a_modality')}
        </span>
      </ModuleHeader>
      <ModuleBody status={status}>
        {isFetching ? (
          <div className="alert alert-info" role="alert">
            <i className="far fa-clock mr-2" aria-hidden="true"></i>
            {i18n.t('tourcms_component_module.searching_modalities')}
          </div>
        ) : (
          <React.Fragment>
            <LanguageFilter
              languages={languages}
              languageFilter={languageFilter}
              setLanguageFilter={setLanguageFilter}
            />
            {components &&
              <ComponentSelect
                components={filteredComponents}
                component={component}
                onComponentSelected={onComponentSelected}
              />
            }

            {!components &&
              <div className="alert alert-info" role="alert">
                <i className="fa fa-warning mr-2" aria-hidden="true"></i>
                {i18n.t('tourcms_component_module.no_components')}
              </div>
            }
          </React.Fragment>
        )}
      </ModuleBody>
      <ModuleFooter {...props}/>
    </ModulePanel>
  )
}

export default TourcmsComponentModule
